import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import { Brand, FeatureFlag, ResolverTag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useResolver from '@swf/core/hooks/useResolver';
import LegacyRAMProvider from '@swf/core/providers/LegacyRAMProvider';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { canUseLocalStorage } from '@swf/core/utils';

import Placeholder from './Placeholder';
import { legacySportWidgetModel, useLegacySportWidget } from './model';
import { useSportsBookExchangeAPI } from './sportsbook/index';

const psDomainMap = {
  USMI: 'mi',
  USPA: 'pa',
  USNJ: 'nj',
  USCO: 'co',
  CAON: 'on'
};

const LegacySportsWidget: WidgetFC = () => {
  const ats = canUseLocalStorage ? JSON.parse(localStorage.getItem('ats') || '{}') : {};
  const { widgetSelect } = useLegacySportWidget();
  const { version, serviceAvailable } = useSelector(widgetSelect.get);
  const [displayMaintenance, setDisplayMaintenance] = useState(false);
  const enableATSMaintenance = useFeature(FeatureFlag.EnableATSMaintenance);
  const bootstrapUrl = useResolver(ResolverTag.ATSBootstrap);
  const sportCommonCssUrl = useResolver(ResolverTag.ATSCss);
  const casinoAssetWrapper = useResolver(ResolverTag.CasinoAssetWrapper);
  const baseUrlTag = useResolver(ResolverTag.ATSBaseUrl);
  const brand = useSelector(select.site.getBrand);
  const additionalTheme = brand === Brand.FoxBet ? 'foxbet-' : '';
  const baseUrl = new URL(ats.host || baseUrlTag);
  const app = ats.app || `js/app-${version}.js`;
  const sportScript = baseUrl.set('pathname', app).toString();
  const css = ats.css || `css/styles-${additionalTheme}${version}.css`;
  const themeStyle = baseUrl.set('pathname', css).toString();
  const siteName = useSelector(select.site.getName);
  const region = useSelector(select.site.getRegion);
  const psDomain = region || psDomainMap[siteName] || siteName;
  const fromEvent = useFromEvent();

  useSportsBookExchangeAPI();

  useEffect(() => {
    fromEvent(document, 'displayATSMaintenance').subscribe(() => setDisplayMaintenance(true));
  }, [fromEvent]);

  if (!serviceAvailable || displayMaintenance || enableATSMaintenance)
    return <ServiceUnavailable />;

  return (
    <LegacyRAMProvider>
      <Helmet
        link={[
          ...(brand === Brand.FoxBet
            ? [
                { href: bootstrapUrl, rel: 'stylesheet', type: 'text/css' },
                { href: sportCommonCssUrl, rel: 'stylesheet', type: 'text/css' }
              ]
            : [])
        ]}
        script={[
          {
            type: 'text/javascript',
            innerHTML: `window.PSDomain = "${psDomain.toLowerCase()}"`
          },
          {
            src: casinoAssetWrapper,
            type: 'text/javascript',
            'data-ot-ignore': ''
          }
        ]}
      />
      {baseUrl && (
        <Helmet
          script={[
            {
              type: 'text/javascript',
              innerHTML: `window.ps = Object.assign(window.ps || {}, {sports: {baseUrl: "${baseUrl.host}"}})`
            }
          ]}
        />
      )}
      {(version || ats.host) && (
        <Helmet
          link={[{ href: themeStyle, rel: 'stylesheet', type: 'text/css' }]}
          script={[{ src: sportScript, type: 'text/javascript', 'data-ot-ignore': '' }]}
        />
      )}
      <div id="content" className="sportsbook-background">
        <div id="contentAreaSingle">
          <Placeholder />
          <div id="container" />
          <div id="modals" />
          <div className="clear" />
        </div>
        <div className="clear" />
      </div>
    </LegacyRAMProvider>
  );
};

LegacySportsWidget.model = legacySportWidgetModel;

export default LegacySportsWidget;
